import { action, computed, observable } from 'mobx'
import { type ActivationLifecycle } from '@/infra/activation'
import {
  type FiltersViewModel,
  type SerializedFilters,
} from '@/infra/FiltersViewModel'
import { type JobFilter } from '@/features/jobs/list/JobSearchViewModel'
import { type SavedJobFilterChanges } from '@/features/jobs/list/saved-job-filters'

/**
 * The default a tab starts from, as its editor shows it.
 */
export interface SavedJobFilterEditorBase {
  name: string
  /**
   * The filters the default sets, which the tab can't set again.
   */
  keys: string[]
}

/**
 * Editing one of the viewer's own tabs: its name and the filters it adds,
 * away from the filter bar.
 */
export class SavedJobFilterEditorViewModel implements ActivationLifecycle {
  @observable accessor name: string

  /**
   * The tab's own filters as they're being edited.
   */
  readonly filters: FiltersViewModel<JobFilter>

  constructor(
    readonly savedFilterId: string,
    name: string,
    filters: SerializedFilters,
    readonly base: SavedJobFilterEditorBase | null,
    createFilters: (filters: SerializedFilters) => FiltersViewModel<JobFilter>,
    private readonly _save: (changes: SavedJobFilterChanges) => void,
  ) {
    this.name = name
    this.filters = createFilters(filters)
  }

  /**
   * Whether the edits can be saved.
   */
  @computed
  get canSave() {
    return this.name.trim() !== ''
  }

  /**
   * Filters that can be added: visible, not added yet, and not already set
   * by the default the tab starts from.
   */
  @computed
  get addableFilters() {
    return this.filters.availableFilters.filter(
      (f) => f.isVisible() && !f.current && !this.base?.keys.includes(f.key),
    )
  }

  /** @inheritDoc */
  activate() {
    this.filters.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this.filters.deactivate()
  }

  @action.bound
  setName(name: string) {
    this.name = name
  }

  /**
   * Adds a filter with its value picker open.
   */
  @action.bound
  addFilter(key: string) {
    this.stopEditingValues()
    this.filters.addFilter(key as keyof JobFilter)
  }

  @action.bound
  removeFilter(key: string) {
    this.filters.clearFilter(key as keyof JobFilter)
  }

  /**
   * Swaps a filter for another, with the new one's value picker open.
   */
  @action.bound
  replaceFilter(key: string, withKey: string) {
    this.filters.clearFilter(key as keyof JobFilter)
    this.addFilter(withKey)
  }

  /**
   * Opens one filter's value picker, closing any other.
   */
  @action.bound
  editValue(key: string) {
    for (const filter of this.filters.explicitFilterVms) {
      if (filter.key === key) {
        filter.startEditing()
      } else if (filter.editing) {
        filter.stopEditing()
      }
    }
  }

  /**
   * Closes the open value picker. A filter left without a value goes away.
   */
  @action.bound
  stopEditingValues() {
    for (const filter of this.filters.explicitFilterVms) {
      if (filter.editing) {
        filter.stopEditing()
      }
    }
  }

  /**
   * Saves the name and filters to the tab.
   */
  @action.bound
  save() {
    if (!this.canSave) {
      return
    }
    this.stopEditingValues()
    this._save({
      name: this.name.trim(),
      filters: this.filters.serializeExplicit(),
    })
  }
}
